import axios from "axios";
import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { apiBaseURL } from "@/lib/api-config";
import { AUTH_PERSIST_KEY, POS_HOLDS_STORAGE_KEY } from "@/lib/client-storage-keys";
import type { User } from "@/lib/types/user";

type AuthState = {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  setSession: (payload: {
    user: User;
    accessToken: string;
    refreshToken: string;
  }) => void;
  setTokens: (payload: { accessToken: string; refreshToken: string }) => void;
  setUser: (user: User | null) => void;
  login: (email: string, password: string) => Promise<void>;
  /** Revokes refresh on server, clears persisted auth + POS holds. */
  logout: () => Promise<void>;
  refreshTokenFn: () => Promise<void>;
};

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,

      setSession: ({ user, accessToken, refreshToken }) =>
        set({ user, accessToken, refreshToken }),

      setTokens: ({ accessToken, refreshToken }) =>
        set({ accessToken, refreshToken }),

      setUser: (user) => set({ user }),

      login: async (email, password) => {
        const { data } = await axios.post<{
          user: User;
          access_token: string;
          refresh_token: string;
        }>(`${apiBaseURL}/api/auth/login`, { email, password });

        set({
          user: data.user,
          accessToken: data.access_token,
          refreshToken: data.refresh_token,
        });
      },

      logout: async () => {
        const { refreshToken: rt, accessToken: at } = get();
        const url = `${apiBaseURL}/api/auth/logout`;
        try {
          if (rt) {
            await axios.post(
              url,
              { refresh_token: rt },
              {
                headers: at ? { Authorization: `Bearer ${at}` } : {},
              },
            );
          }
        } catch {
          // still wipe client
        }
        set({ user: null, accessToken: null, refreshToken: null });
        try {
          useAuthStore.persist?.clearStorage?.();
        } catch {
          // ignore
        }
        if (typeof localStorage !== "undefined") {
          try {
            localStorage.removeItem(AUTH_PERSIST_KEY);
            localStorage.removeItem(POS_HOLDS_STORAGE_KEY);
          } catch {
            // ignore
          }
        }
      },

      refreshTokenFn: async () => {
        const rt = get().refreshToken;
        if (!rt) {
          throw new Error("No refresh token");
        }
        const { data } = await axios.post<{
          access_token: string;
          refresh_token: string;
        }>(`${apiBaseURL}/api/auth/refresh`, { refresh_token: rt });

        set({
          accessToken: data.access_token,
          refreshToken: data.refresh_token,
        });
      },
    }),
    {
      name: AUTH_PERSIST_KEY,
      storage: createJSONStorage(() => localStorage),
      partialize: (s) => ({
        user: s.user,
        accessToken: s.accessToken,
        refreshToken: s.refreshToken,
      }),
    },
  ),
);
